import { API_ENDPOINTS, axiosInstance } from "config/api";
import { cachedApiCall, getCacheKey, clearCacheEntry } from '../utils/apiCache';

/**
 * Fetches parameters for a specific doctor
 * @param {number} doctorId - The ID of the doctor
 * @returns {Promise<Array>} - List of parameter definitions
 */
export const fetchParametersByDoctor = async (doctorId) => {
  try {
    console.log("the fetching of the paramerts for doc Id:", doctorId);
    const response = await axiosInstance.get(
      API_ENDPOINTS.PARAMETERS.FETCH_BY_DOCTOR,
      {
        params: { doctor_id: doctorId },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching parameters by doctor:", error);
    throw error;
  }
};

/**
 * Loads appointment parameters from localStorage for offline access
 * 
 * This function provides client-side storage retrieval for appointment parameters,
 * enabling offline functionality and reducing API calls for frequently accessed data.
 * 
 * Key Features:
 * - Namespace-based storage using appointment ID
 * - JSON parsing with error handling
 * - Graceful fallback to null on errors
 * - Console error logging for debugging
 * 
 * Storage Format:
 * - Key: `parameters_${appointmentId}`
 * - Value: JSON stringified parameters object
 * 
 * @param {number} appointmentId - The ID of the appointment
 * @returns {Object|null} - The parameters object or null if not found/error
 * @example
 * const params = loadAppointmentParametersFromLocalStorage(123);
 * if (params) { console.log('Loaded parameters:', params); }
 */
export const loadAppointmentParametersFromLocalStorage = (appointmentId) => {
  try {
    const data = localStorage.getItem(`parameters_${appointmentId}`);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Error loading parameters from localStorage:", error);
    return null;
  }
};

/**
 * Saves appointment parameters to localStorage for offline access
 * 
 * This function provides client-side storage persistence for appointment parameters,
 * creating a local backup that can be accessed without network connectivity.
 * 
 * Key Features:
 * - Namespace-based storage using appointment ID
 * - JSON serialization with error handling
 * - Silent failure with error logging
 * - Automatic data persistence across browser sessions
 * 
 * Storage Strategy:
 * - Overwrites existing data for the same appointment
 * - Uses structured key naming for easy retrieval
 * - Handles complex objects through JSON serialization
 * 
 * @param {number} appointmentId - The ID of the appointment
 * @param {Object} parameters - The parameters object to save
 * @example
 * const params = { temperature: 98.6, bloodPressure: "120/80" };
 * saveAppointmentParametersToLocalStorage(123, params);
 */
export const saveAppointmentParametersToLocalStorage = (
  appointmentId,
  parameters
) => {
  try {
    localStorage.setItem(
      `parameters_${appointmentId}`,
      JSON.stringify(parameters)
    );
  } catch (error) {
    console.error("Error saving parameters to localStorage:", error);
  }
};

/**
 * Fetches parameter values for a specific appointment
 * Uses caching to prevent duplicate API calls
 * @param {number} appointmentId - The ID of the appointment
 * @param {Object} options - Cache options { force: boolean, ttl: number }
 * @returns {Promise<Array>} - List of parameter values
 */
/**
 * Fetches parameter values for a specific appointment
 * Uses caching to prevent duplicate API calls
 * @param {number} appointmentId - The ID of the appointment
 * @param {Object} options - Cache options { force: boolean, ttl: number }
 * @returns {Promise<Array>} - List of parameter values
 */
export const fetchParametersByAppointment = async (appointmentId, options = {}) => {
  // Generate cache key for this specific appointment
  const cacheKey = getCacheKey('parameters/byAppointment', { appointment_id: appointmentId });

  // Use cached API call with 2-minute TTL (shorter than default for medical data)
  const ttl = options.ttl || 2 * 60 * 1000; // 2 minutes

  // Add retries to handle transient network errors
  const retries = options.retries !== undefined ? options.retries : 2;

  return cachedApiCall(
    cacheKey,
    async () => {
      try {
        const response = await axiosInstance.get(
          API_ENDPOINTS.PARAMETERS.BY_APPOINTMENT,
          {
            params: { appointment_id: appointmentId },
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching parameters by appointment:", error);
        throw error;
      }
    },
    { ttl, force: options.force || false, retries }
  );
};

/**
 * Saves parameter values for an appointment
 * Invalidates cache after successful save to ensure fresh data on next fetch
 * @param {number} appointmentId - The ID of the appointment
 * @param {Array} parameters - Array of parameter objects with parameter_id and value
 * @returns {Promise<Object>} - API response
 */
export const saveAppointmentParameters = async (appointmentId, parameters) => {
  try {
    const response = await axiosInstance.post(
      API_ENDPOINTS.PARAMETERS.ADD_APPOINTMENT,
      {
        appointment_id: appointmentId,
        parameters: parameters,
      }
    );

    // Invalidate cache for this appointment after successful save
    const cacheKey = getCacheKey('parameters/byAppointment', { appointment_id: appointmentId });
    clearCacheEntry(cacheKey);
    console.log(`[ParametersService] Cache invalidated for appointment ${appointmentId}`);

    return response.data;
  } catch (error) {
    console.error("Error saving appointment parameters:", error);
    throw error;
  }
};

/**
 * Save parameters stored in localStorage for the appointment to the server.
 * This is used to flush any locally edited parameter values when the
 * consultation is being ended by the user (End Consultation).
 *
 * Returns the API response or null if nothing to save.
 */
export const flushLocalParametersToServer = async (appointmentId) => {
  try {
    if (!appointmentId) return null;
    const raw = localStorage.getItem(`parameters_${appointmentId}`);
    if (!raw) return null;
    const paramsObj = JSON.parse(raw || "{}") || {};

    const parametersArray = Object.entries(paramsObj)
      .filter(([, value]) => value !== "" && value !== null && value !== undefined)
      .map(([parameter_id, value]) => ({
        parameter_id: parseInt(parameter_id, 10),
        value: String(value).trim(),
      }));

    if (parametersArray.length === 0) return null;

    const res = await saveAppointmentParameters(appointmentId, parametersArray);
    // If save succeeded, remove local cache
    try {
      localStorage.removeItem(`parameters_${appointmentId}`);
    } catch (e) {
      console.warn('Could not remove local parameters cache after flush', e);
    }
    return res;
  } catch (error) {
    console.error("Error flushing local parameters to server:", error);
    throw error;
  }
};
